import React, { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip } from '@origo/origo-ui';
import { CaretDownIcon, WarningIcon } from '@phosphor-icons/react';
import type { ProcessingFormValues, ProcessingItem } from '../../../types';
import ProcessingForm, { type ProcessingFieldErrors, type ProcessingFormOptions } from './ProcessingForm';
import styles from './ProcessingBlock.module.css';

interface ProcessingBlockProps {
    item: ProcessingItem;
    options: ProcessingFormOptions;
    fieldErrors?: ProcessingFieldErrors;
    defaultOpen?: boolean;
    /** Kalles når brukeren klikker Lagre med gjeldende draft. */
    onSave: (values: ProcessingFormValues) => void;
    onDelete: () => void;
    /** Åpner sidepanelet med tilknyttede dokumenter for denne behandlingen (#14686). */
    onShowDocuments: (planbehUid: string) => void;
    isSaving?: boolean;
}

const FIELDS: ReadonlyArray<keyof ProcessingFormValues> = [
    'navn', 'planbehandlingtype', 'dato', 'fradato', 'tildato', 'saksnummer', 'beskrivelse',
];

const ProcessingBlock: React.FC<ProcessingBlockProps> = ({
    item,
    options,
    fieldErrors,
    defaultOpen = false,
    onSave,
    onDelete,
    onShowDocuments,
    isSaving,
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(defaultOpen);
    const [draft, setDraft] = useState<ProcessingFormValues>(item.values);
    const contentId = useId();

    const isDirty = useMemo(
        () => FIELDS.some(f => draft[f] !== item.values[f]),
        [draft, item.values]
    );

    // Sync draft når backend-data endres (f.eks. etter lagring som refetcher),
    // men aldri over brukerens ulagrede endringer.
    useEffect(() => {
        if (!isDirty) setDraft(item.values);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.values]);

    const handleChange = (field: keyof ProcessingFormValues, value: string) => {
        setDraft(prev => ({ ...prev, [field]: value }));
    };

    const handleCancel = () => {
        setDraft(item.values);
        setOpen(false);
    };

    return (
        <section className={`${styles.block} ${open ? styles.open : ''}`}>
            <button
                type="button"
                className={styles.trigger}
                aria-expanded={open}
                aria-controls={contentId}
                onClick={() => setOpen(o => !o)}
            >
                <span className={styles.headingGroup}>
                    <span className={styles.heading}>{item.tittel}</span>
                    <span className={styles.meta}>
                        {item.dato && <span>{item.dato}</span>}
                        {item.saksnummer && <span>{item.saksnummer}</span>}
                    </span>
                </span>
                <span className={styles.badges}>
                    {item.missingDate && (
                        <Chip variant="warning">
                            <WarningIcon size={14} weight="bold" aria-hidden />
                            {t('processings.missingDate')}
                        </Chip>
                    )}
                    {isDirty && <Chip variant="default">{t('processings.unsaved')}</Chip>}
                    <Chip variant={item.typeBadge.variant ?? 'default'}>{item.typeBadge.label}</Chip>
                </span>
                <CaretDownIcon
                    size={20}
                    className={`${styles.indicator} ${open ? styles.indicatorOpen : ''}`}
                    aria-hidden
                />
            </button>
            <div id={contentId} role="region" aria-label={item.tittel} hidden={!open}>
                <ProcessingForm
                    values={draft}
                    options={options}
                    fieldErrors={fieldErrors}
                    planbehUid={item.id}
                    documentCount={item.documentCount}
                    onShowDocuments={() => onShowDocuments(item.id)}
                    onChange={handleChange}
                    onCancel={handleCancel}
                    onSave={() => onSave(draft)}
                    onDelete={onDelete}
                    saveDisabled={!isDirty}
                    isSaving={isSaving}
                />
            </div>
            {/* Hidden text for screen readers describing toggle action */}
            <span className={styles.srOnly}>
                {open ? t('common.close') : t('common.edit')}
            </span>
        </section>
    );
};

export default ProcessingBlock;
